// MRZ TD1 (3x30) generator - JavaScript
// Input: cccd12 (string 12 digits), dob (string), name (string), sex ('M'/'F'), expiry (string), issuingCountry (default 'VNM')
// Output: { line1, line2, line3, allLines }

export function removeDiacritics(str) {
  // remove diacritics using Unicode normalization
  return str.normalize('NFKD').replace(/[\u0300-\u036f]/g, '');
}

export function toMRZChar(ch) {
  // allowed: A-Z, 0-9, <. Other chars will be converted to '<' or removed.
  if (ch >= 'A' && ch <= 'Z') return ch;
  if (ch >= '0' && ch <= '9') return ch;
  if (ch === '<') return '<';
  return '<';
}

export function normalizeFieldAlphaNumeric(s) {
  // remove diacritics, uppercase, keep only A-Z0-9 and spaces (spaces -> <)
  s = removeDiacritics(s).toUpperCase();
  // replace any character not A-Z0-9 with space (so later convert space-><)
  s = s.replace(/[^A-Z0-9 ]/g, ' ');
  s = s.replace(/ +/g, ' '); // collapse spaces
  s = s.trim();
  s = s.replace(/ /g, '<');
  // convert any remaining invalid char to '<' (safety)
  s = s.split('').map(ch => toMRZChar(ch)).join('');
  return s;
}

export function charValueMRZ(ch) {
  if (ch === '<') return 0;
  if (ch >= '0' && ch <= '9') return ch.charCodeAt(0) - '0'.charCodeAt(0);
  if (ch >= 'A' && ch <= 'Z') return 10 + (ch.charCodeAt(0) - 'A'.charCodeAt(0));
  throw new Error('Invalid MRZ character: ' + ch);
}

export function computeCheckDigit(field) {
  const weights = [7, 3, 1];
  let total = 0;
  for (let i = 0; i < field.length; i++) {
    const ch = field[i];
    const val = charValueMRZ(ch);
    const w = weights[i % 3];
    total += val * w;
  }
  return String(total % 10);
}

export function padRight(s, length) {
  if (s.length > length) return s.slice(0, length);
  return s + '<'.repeat(length - s.length);
}

export function formatDateToYYMMDD(input) {
  // Accepts YYYY-MM-DD or DD/MM/YYYY or D/M/YYYY or ISO date strings
  if (!input) return null;
  input = input.trim();
  
  // Handle ISO date strings (e.g., "2024-01-01T18:19:46.892Z")
  if (input.includes('T') || input.includes('Z')) {
    const date = new Date(input);
    if (isNaN(date.getTime())) return null;
    const year = date.getFullYear().toString().slice(-2);
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return year + month + day;
  }
  
  // YYYY-MM-DD
  const isoMatch = input.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    return isoMatch[1].slice(2) + isoMatch[2] + isoMatch[3];
  }
  
  // DD/MM/YYYY or D/M/YYYY
  const slashMatch = input.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slashMatch) {
    const dd = slashMatch[1].padStart(2, '0');
    const mm = slashMatch[2].padStart(2, '0');
    const yyyy = slashMatch[3];
    return yyyy.slice(2) + mm + dd;
  }
  
  throw new Error('Unsupported date format: ' + input + ' (use YYYY-MM-DD, DD/MM/YYYY, or ISO date)');
}

/**
 * Main function: generate MRZ TD1 (3x30)
 * params:
 *  - cccd12: string of 12 digits (required)
 *  - name: full name string (required) -> will be split: surname = first token, given = rest
 *  - dob: date of birth (YYYY-MM-DD or DD/MM/YYYY or ISO date)
 *  - sex: 'M' or 'F'
 *  - expiry: expiry date (YYYY-MM-DD or DD/MM/YYYY or ISO date)
 *  - issuingCountry: default 'VNM'
 */
export function generateMRZ_TD1({ cccd12, name, dob, sex = 'M', expiry, issuingCountry = 'VNM' }) {
  console.log('MRZ Input:', { cccd12, name, dob, sex, expiry, issuingCountry });
  
  if (!/^\d{12}$/.test(cccd12)) {
    throw new Error('cccd12 must be 12 digits, got: ' + cccd12);
  }
  
  // docnum9 = digits 4..12 (0-based slice(3,12))
  const docnum9 = cccd12.slice(3, 12); // 9 chars
  const docnum9_field = docnum9; // should be numeric string length 9
  const docnum_cd = computeCheckDigit(docnum9_field);

  // optional15 = full12 + '<<' + check(full12) -> 12 + 2 + 1 = 15
  const full12 = cccd12;
  const full12_cd = computeCheckDigit(full12);
  const optional15 = full12 + '<<' + full12_cd; // exactly 15 chars

  // dob and expiry -> YYMMDD
  const dob6 = formatDateToYYMMDD(dob);
  const dob_cd = computeCheckDigit(dob6);

  const expiry6 = formatDateToYYMMDD(expiry);
  const expiry_cd = computeCheckDigit(expiry6);

  console.log('MRZ Computed:', { docnum9_field, docnum_cd, dob6, dob_cd, expiry6, expiry_cd });

  // Line 1:
  // 1-2: ID
  // 3-5: issuingCountry
  // 6-14: docnum9 (9)
  // 15: docnum_cd
  // 16-30: optional15 (15 chars)
  const line1 = padRight(
    ('ID' + issuingCountry + docnum9_field + docnum_cd + optional15),
    30
  ).slice(0, 30);

  // Line 2:
  // 1-6: dob6
  // 7: dob_cd
  // 8: sex
  // 9-14: expiry6
  // 15: expiry_cd
  // 16-18: nationality (issuingCountry)
  // 19-29: filler '<' (11 chars)
  // 30: composite check digit (to be computed)
  const partBeforeComposite = dob6 + dob_cd + (sex ? sex.toUpperCase() : '<') + expiry6 + expiry_cd + issuingCountry;
  const filler11 = '<'.repeat(11);
  // composite_input is docnum9 + docnum_cd + optional15 + dob6 + dob_cd + expiry6 + expiry_cd
  const composite_input = docnum9_field + docnum_cd + optional15 + dob6 + dob_cd + expiry6 + expiry_cd;
  const composite_cd = computeCheckDigit(composite_input);

  const line2 = padRight(partBeforeComposite + filler11 + composite_cd, 30).slice(0, 30);

  // Line 3: name field - SURNAME<<GIVEN<NAMES padded to 30
  // Heuristic for Vietnamese name: surname = first token, rest = given names
  const nameNorm = removeDiacritics(name).toUpperCase().trim();
  const tokens = nameNorm.split(/\s+/);
  let surname = tokens[0] || '';
  let given = tokens.slice(1).join('<') || '';
  // if no given part, just keep surname
  let nameFieldRaw = surname + '<<' + given;
  // normalize characters (only A-Z and < allowed)
  nameFieldRaw = nameFieldRaw.replace(/[^A-Z<]/g, '<');
  const line3 = padRight(nameFieldRaw, 30).slice(0, 30);

  return {
    line1,
    line2,
    line3,
    composite_input,
    computed: {
      docnum9_field,
      docnum_cd,
      full12,
      full12_cd,
      dob6,
      dob_cd,
      expiry6,
      expiry_cd,
      composite_cd
    },
    allLines: [line1, line2, line3].join('\n')
  };
}
